import {
  Add,
  Archive,
  KeyboardArrowDown,
  KeyboardArrowRight,
  OpenInNew,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  // Grid, // Removed Grid import
  Menu, // Add Menu import
  MenuItem, // Add MenuItem import
  Paper,
  Stack,
  styled,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import Grid from '@mui/material/Grid2'; // Add Grid2 import
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import AssigneeDropdown from "../../utils/AssigneeDropdown";
import CreateProjectDialog from "../dialogs/ProjectDialog";
import CreateTrackDialog from "../dialogs/TrackDialog";
import TrackDepartmentSelects from "../KeyComponents/TrackDepartmentSelects";
import { useWorkspace } from "../../utils/WorkspaceContext";
import useDebounce from "../../hooks/debounceHook";

const StyledPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0.1),
  padding: theme.spacing(0.1),
}));

const TrackHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1),
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TaskRow = styled(Grid)(({ theme }) => ({
  // display: "grid", // Removed
  // gridTemplateColumns: "0.8fr 182px 7fr 2fr 2fr 2fr", // Removed
  // gap: theme.spacing(2), // Will be handled by spacing prop on Grid container
  alignItems: "center",
  padding: theme.spacing(1),
  marginLeft: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderRow = styled(Grid)(({ theme }) => ({
  // display: "grid", // Removed
  // gridTemplateColumns: "0fr 182px 7fr 2fr 2fr 2fr", // Removed
  // gap: theme.spacing(2), // Will be handled by spacing prop on Grid container
  padding: theme.spacing(1),
  marginLeft: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: "bold",
  alignItems: "center",
}));

const ColorBar = styled(Box)(({ color }) => ({
  width: "4px",
  height: "100%",
  backgroundColor: color,
  borderRadius: "2px",
  marginRight: "8px",
}));

const TrackListView = () => {
  const [tracks, setTracks] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [openTrackDialog, setOpenTrackDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Add new state for context menu
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);

  // Add context menu handlers
  const handleContextMenu = (event, track) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setSelectedTrack(track);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedTrack(null);
  };

  const getTrackArchiveErrorMessage = (error) => {
    const data = error?.response?.data;
    if (!data) return "Error archiving track.";

    if (typeof data === "string") return data;

    const detail = data.detail || data.error;
    if (detail && Array.isArray(data.non_compliant_tasks)) {
      const taskLabels = data.non_compliant_tasks
        .map((task) => {
          const name = task?.name || "(unnamed)";
          const status = task?.status ? ` - ${task.status}` : "";
          return `${name}${status}`;
        })
        .filter(Boolean);

      if (taskLabels.length > 0) {
        const preview = taskLabels.slice(0, 3).join(", ");
        const suffix =
          taskLabels.length > 3
            ? ` (+${taskLabels.length - 3} more)`
            : "";
        return `${detail} Non-compliant tasks: ${preview}${suffix}`;
      }

      return detail;
    }

    if (detail) return detail;

    if (typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      if (firstKey) {
        const firstValue = data[firstKey];
        if (Array.isArray(firstValue) && firstValue[0]) {
          return String(firstValue[0]);
        }
      }
    }

    return "Error archiving track.";
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbarState({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteTrack = async () => {
    if (!selectedTrack?.id) return;

    try {
      await api.delete(`tracks/${selectedTrack.id}/`, {
        params: { workspace_id: currentWorkspace.id },
      });
      // Refresh the tracks list
      fetchProjects();
      showSnackbar('Track archived successfully.', 'success');
    } catch (error) {
      showSnackbar(getTrackArchiveErrorMessage(error), "error");
      console.error("Error deleting track:", error);
    }
    handleCloseContextMenu();
  };

  const debouncedUpdateTrack = useDebounce(async (trackId, updates) => {
    try {
      await api.patch(`tracks/${trackId}/`, {
        ...updates,
        workspace: currentWorkspace.id,
      });
      fetchProjects();
    } catch (error) {
      console.error("Error updating track:", error, "Updates:", updates);
    }
  });

  const fetchProjects = async () => {
    try {
      const response = await api.get("grouped-tracks/", {
        params: { workspace_id: currentWorkspace.id },
      });
      setTracks(response.data.payload);
    } catch (error) {
      console.error("Error fetching tracks:", error);
    }
  };

  const fetchDepartmentsAndDivisions = async () => {
    try {
      const params = { workspace_id: currentWorkspace.id };
      const [departmentsRes, divisionsRes] = await Promise.all([
        api.get("departments/", { params }),
        api.get("divisions/", { params }),
      ]);
      setDepartments(departmentsRes.data);
      setDivisions(divisionsRes.data);
    } catch (error) {
      console.error("Error fetching departments/divisions:", error);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchDepartmentsAndDivisions();
  }, []);

  const toggleProject = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const handleCreateProject = () => {
    setOpenProjectDialog(true);
  };

  const handleCreateTrack = (projectId) => {
    console.log(projectId);
    setSelectedProject(projectId);
    setOpenTrackDialog(true);
  };

  const handleTrackUpdate = async (trackId, field, value) => {
    // Update UI immediately
    setTracks((prevTracks) =>
      prevTracks.map((project) => ({
        ...project,
        tracks: project.tracks?.map((track) =>
          track.id === trackId ? { ...track, [field]: value } : track
        ),
      }))
    );

    // Debounced API call
    debouncedUpdateTrack(trackId, { [field]: value });
  };

  // Department/division go out immediately rather than through the shared
  // debounce, which would drop one of the two when a department change also
  // clears the division.
  const handleDepartmentChange = async (trackId, updates) => {
    setTracks((prevTracks) =>
      prevTracks.map((project) => ({
        ...project,
        tracks: project.tracks?.map((track) =>
          track.id === trackId ? { ...track, ...updates } : track
        ),
      }))
    );
    try {
      await api.patch(`tracks/${trackId}/`, {
        ...updates,
        workspace: currentWorkspace.id,
      });
    } catch (error) {
      showSnackbar("Error updating track department/division.");
      fetchProjects();
    }
  };

  const handleAssigneeChange = (trackId, userId) => {
    handleTrackUpdate(trackId, "assignee", userId);
  };

  const handleAddTrack = (projectId) => {
    console.log(projectId);
    handleCreateTrack(projectId);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateProject}
        >
          Create Project
        </Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateTrack}
        >
          Create Track
        </Button>
      </Stack>

      <StyledPaper elevation={1}>
        {tracks.map((project) => (
          <Box key={project.id}>
            <TrackHeader onClick={() => toggleProject(project.project_id)}>
              {expandedProjects[project.project_id] ? (
                <KeyboardArrowDown />
              ) : (
                <KeyboardArrowRight />
              )}
              <Typography variant="h6">{project.project_name}</Typography>
            </TrackHeader>

            {expandedProjects[project.project_id] && (
              <>
                <HeaderRow container spacing={2}>
                  <Grid item size={0.5}>
                    <Typography>Action</Typography>
                  </Grid>
                  <Grid item size={3.5}>
                    <Typography>Track Name</Typography>
                  </Grid>
                  <Grid item size={1}>
                    <Typography>Assignee</Typography>
                  </Grid>
                  <Grid item size={3}>
                    <Typography>Description</Typography>
                  </Grid>
                  <Grid item size={2}>
                    <Typography>Department</Typography>
                  </Grid>
                  <Grid item size={2}>
                    <Typography>Division</Typography>
                  </Grid>
                </HeaderRow>

                {project.tracks?.map((track) => (
                  <TaskRow 
                    container 
                    spacing={2} 
                    key={track.id}
                    onContextMenu={(e) => handleContextMenu(e, track)} // Add context menu handler
                  >
                    <Grid item size={0.5}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tracks/${track.id}`);
                        }}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Grid>
                    <Grid item size={3.5}>
                      <TextField
                        fullWidth
                        variant="standard"
                        value={track.name}
                        onChange={(e) =>
                          handleTrackUpdate(track.id, "name", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item size={1}>
                      <AssigneeDropdown
                        currentSelected={track.assignee}
                        onChange={(userId) =>
                          handleAssigneeChange(track.id, userId)
                        }
                      />
                    </Grid>
                    <Grid item size={3}>
                      <TextField
                        fullWidth
                        variant="standard"
                        value={track.description || ""}
                        onChange={(e) =>
                          handleTrackUpdate(
                            track.id,
                            "description",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <TrackDepartmentSelects
                      departments={departments}
                      divisions={divisions}
                      value={track}
                      onChange={(updates) =>
                        handleDepartmentChange(track.id, updates)
                      }
                      fieldProps={{
                        fullWidth: true,
                        variant: "standard",
                        label: undefined,
                      }}
                      wrapField={(field) => (
                        <Grid item size={2}>
                          {field}
                        </Grid>
                      )}
                    />
                  </TaskRow>
                ))}
                <Button
                  sx={{ mt: 1, ml: 4 }}
                  onClick={() => handleAddTrack(project.project_id)}
                >
                  + Add Track
                </Button>
              </>
            )}
          </Box>
        ))}
      </StyledPaper>

      {/* Add the context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={handleDeleteTrack}
          sx={{
            color: "error.main",
            "&:hover": { bgcolor: "error.light" },
          }}
        >
          <Archive sx={{ mr: 1 }} fontSize="small" />
          Archive Track
        </MenuItem>
      </Menu>

      <CreateProjectDialog
        open={openProjectDialog}
        onClose={() => setOpenProjectDialog(false)}
        onSuccess={() => {
          setOpenProjectDialog(false);
          fetchProjects();
        }}
      />

      {console.log(selectedProject)}
      <CreateTrackDialog
        projectId={selectedProject}
        open={openTrackDialog}
        onClose={() => setOpenTrackDialog(false)}
        onSuccess={() => {
          setOpenTrackDialog(false);
          fetchProjects();
        }}
      />

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarState.severity}
          sx={{ width: "100%" }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrackListView;
